import { useMemo } from "react";
import { Loading } from "@/components/atoms";
import { DataTable, Pagination, NoSearchResults, TABLE_MAXH_SIMPLE } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePagination, useProfile } from "@/hooks";
import { useReactivateAccountMutation } from "@/redux/apis/client/clientApi";
import { useAppSelector } from "@/redux/hooks";
import { clientsPageSelector } from "@/redux/slices/client/selectors";
import { Client } from "@/redux/slices/client/types";
import { addToast, clsx } from "@/utils";
import { ClientsEmptyState } from "./components/ClientsEmptyState";
import { useClientsContext } from "./context";
import { generateClientColumns, generateClientData } from "./helpers";
import { useGetClientsParams } from "./useGetClientsParams";

interface ClientsTableProps extends React.HTMLAttributes<HTMLDivElement> {
  items: Client[];
}

export const ClientsTable = ({ items, className, ...props }: ClientsTableProps) => {
  const { dateFormat } = useProfile();
  const [reactivateAccount, { isLoading }] = useReactivateAccountMutation();
  const { setStep, setSelectedClient } = useClientsContext();
  const { sorting, setSorting, search, filters } = useGetClientsParams();
  const isFiltersEmpty = Object.values(filters).every((el) => el === undefined);

  const onReactivateClick = (client: Client) => {
    reactivateAccount(client.id)
      .unwrap()
      .then(() => {
        addToast("success", `Reactivated access for ${client.name}`);
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const setPendingRemoveAccess = (client: Client) => {
    setSelectedClient(client);
    setStep("remove-access");
  };

  const columns = useMemo(() => generateClientColumns(), []);
  const data = useMemo(
    () => generateClientData(items, dateFormat, setPendingRemoveAccess, onReactivateClick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const renderEmptyState = () => {
    if (search) return <NoSearchResults />;
    if (!isFiltersEmpty) return <ClientsEmptyState hasFilters />;

    return <ClientsEmptyState />;
  };

  return (
    <div className={clsx("relative flex bg-white", className)}>
      {isLoading && <Loading />}
      <DataTable
        className="-mt-4 w-full [&_table]:table-auto"
        maxHeight={TABLE_MAXH_SIMPLE}
        columns={columns}
        data={data}
        sorting={sorting}
        setSorting={setSorting}
        emptyState={renderEmptyState()}
        {...props}
      />
    </div>
  );
};

const ClientsTablePagination = () => {
  const { current, count, total, size } = useAppSelector(clientsPageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("clients");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

ClientsTable.Pagination = ClientsTablePagination;
